import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Clock, DollarSign, Gauge, MapPin, Users } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { DPS_MEDIA } from "../lib/content";
import { trackEvent } from "../lib/analytics";

const QUICK = [
  { icon: Clock, k: "Session Window", v: "11:30 AM – 3:00 PM · expect 1–2 hours to finish" },
  { icon: Users, k: "Wave Order", v: "Doubles go first, then singles · staggered slots" },
  { icon: DollarSign, k: "Cost", v: "$25 per participant" },
  { icon: MapPin, k: "Location", v: "Dallas Performance Systems · 2651 Fondren Dr, Ste. B, Dallas, TX" },
];

const DETAILS = [
  "An open session covering all 8 HYROX exercises in sequence, plus every 1km run.",
  "Held at Dallas Performance Systems — 2651 Fondren Dr, Ste. B, Dallas, TX 75206.",
  "Instructions are given 10 minutes before your scheduled start — arrive no later than 10 minutes before your selected time.",
  "A warm-up area is available, so feel free to arrive early.",
  "Official weights and reps for your division, held to movement standards and timed.",
];

const EXPERIENCE = [
  { level: "HYROX Veteran", pick: "Choose earlier times", note: "You'll move fastest through the course — going first keeps the lanes clear behind you." },
  { level: "1–2 HYROX Events Done", pick: "Select mid-range times", note: "You know the flow — a middle slot keeps you clear of both the fastest and newest athletes." },
  { level: "Newbies & HYROX Singles", pick: "Opt for later times", note: "Take the pressure off — later waves give you room to learn the course at your own pace." },
];

export default function SimulationFormat() {
  return (
    <div data-testid="simulation-format-page" className="pt-16">
      <Seo
        title="HYROX Full Simulation Format — How Sim Day Runs | HyroxNinja"
        description="How the full HYROX simulation runs at Dallas Performance Systems (2651 Fondren Dr, Ste. B, Dallas): 11:30 AM–3:00 PM window, all 8 exercises in sequence, staggered waves with doubles first, $25 per participant."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.simFormat} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
        <p className="eyebrow mb-4">Full Course Simulation</p>
        <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
          <MaskedLine delay={0.1}>How Sim Day</MaskedLine>
          <MaskedLine delay={0.25} className="text-volt">Actually Runs</MaskedLine>
        </h1>
        <div className="flex flex-wrap gap-3 mb-8" data-testid="sim-format-dates">
          {["SEPT 13", "OCT 25"].map((d) => (
            <span key={d} className="font-head font-black uppercase text-xl sm:text-2xl border border-volt/50 text-volt px-5 py-2.5">{d}</span>
          ))}
        </div>
        <p className="text-zinc-400 max-w-3xl leading-relaxed">
          The full-course HYROX simulation runs between 11:30 AM and 3:00 PM. Expected
          completion time ranges from 1 to 2 hours depending on your pace. This page is the
          complete format guide — read it before you pick your time slot.
        </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {QUICK.map((q, i) => (
            <Reveal key={q.k} delay={i * 0.05} className="bg-[#0c0c0c] p-6 h-full">
              <div data-testid={`sim-format-fact-${i}`}>
                <q.icon size={20} className="text-volt mb-3" />
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{q.k}</p>
                <p className="font-head font-bold uppercase text-base leading-snug">{q.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <Reveal className="h-full">
            <article className="bg-[#0c0c0c] p-7 sm:p-9 h-full" data-testid="sim-format-details-card">
              <p className="eyebrow mb-3">Important Details</p>
              <h2 className="font-head font-extrabold uppercase text-3xl tracking-tight mb-6">What to know</h2>
              <ul className="native-detail-list">
                {DETAILS.map((d) => <li key={d}><Check size={15} /> <span>{d}</span></li>)}
              </ul>
            </article>
          </Reveal>
          <Reveal className="h-full" delay={0.06}>
            <article className="bg-[#0c0c0c] p-7 sm:p-9 h-full" data-testid="sim-format-signup-card">
              <p className="eyebrow mb-3">Sign Up & Waves</p>
              <h2 className="font-head font-extrabold uppercase text-3xl tracking-tight mb-4">Slots & scheduling</h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                Register through the open registrations on the Events page. Time slots are
                staggered — <span className="text-white font-semibold">doubles go first, followed by singles</span>.
                At sign-up you'll self-rate your experience so waves are seeded fairly.
              </p>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                Times may adjust slightly based on responses (singles vs. doubles) and any
                additional slots that open. The final schedule is emailed to everyone by
                Friday morning before the simulation.
              </p>
              <Link to="/events" className="btn-volt" data-testid="sim-format-book-button" onClick={() => trackEvent("booking_click", { placement: "sim_format_page", service: "simulation" })}>
                Book Full Simulation <ArrowRight size={16} />
              </Link>
            </article>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="sim-format-experience-section">
        <Reveal>
          <p className="eyebrow mb-3 flex items-center gap-2"><Gauge size={14} /> Seeding</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-3">
            Choose your time by experience
          </h2>
          <p className="text-zinc-400 max-w-3xl leading-relaxed mb-9">
            To prevent participants from catching up to each other, select a time that aligns
            with your HYROX experience — <span className="text-white font-semibold">experience, not preference</span>.
            If you're about to be lapped by earlier sign-ups, you'll be asked to step aside until
            they pass or go out of order.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {EXPERIENCE.map((e, i) => (
            <Reveal key={e.level} delay={i * 0.06} className="h-full">
              <article className="bg-[#0c0c0c] p-7 h-full" data-testid={`sim-format-experience-${i}`}>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Level {i + 1}</p>
                <h3 className="font-head font-bold uppercase text-xl mb-1">{e.level}</h3>
                <p className="text-volt font-mono2 text-xs uppercase tracking-[0.2em] mb-3">{e.pick}</p>
                <p className="text-zinc-400 text-sm leading-relaxed">{e.note}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <Reveal>
          <div className="border border-white/10 bg-[#0c0c0c] p-8 lg:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-testid="sim-format-questions-card">
            <div>
              <p className="eyebrow mb-2">Questions?</p>
              <p className="text-zinc-300 leading-relaxed">
                Register on the <Link to="/events" className="text-volt hover:text-white transition-colors" data-testid="sim-format-events-link">Events page</Link> and
                reply to your confirmation email with any questions — the Hyrox Ninja team reads every one.
              </p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-600 mt-3">
                Hyrox Ninja @ Dallas Performance Systems · 2651 Fondren Dr, Ste. B, Dallas, TX 75206
              </p>
              <p className="text-zinc-500 text-xs leading-relaxed mt-3" data-testid="sim-format-blog-links">
                Prep reading:{" "}
                <Link to="/blog/hyrox-simulation-workout" className="text-volt hover:text-white transition-colors underline underline-offset-2">what you'll do on sim day, station by station</Link>
                {" "}and{" "}
                <Link to="/blog/half-vs-full-hyrox-simulation" className="text-volt hover:text-white transition-colors underline underline-offset-2">half vs full simulation</Link>.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Link to="/simulation" className="btn-ghost" data-testid="sim-format-back-link">
                Simulation Overview <ArrowUpRight size={14} />
              </Link>
              <Link to="/results" className="btn-ghost" data-testid="sim-format-leaderboard-link">
                Leaderboard <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
